import React from 'react';
import { ClothTypeDef, countDesignCombinations, GenderSetting } from '../config/clothTypes';
import { getClothIcon } from '../clothIcons';

interface DesignerSummaryReviewProps {
  clothImage: string;
  gender: GenderSetting;
  garment?: ClothTypeDef;
  attributes: Record<string, string[]>;
  onRemoveAttribute: (label: string, value: string) => void;
}

const DesignerSummaryReview: React.FC<DesignerSummaryReviewProps> = ({
  clothImage,
  gender,
  garment,
  attributes,
  onRemoveAttribute,
}) => {
  const attributeEntries = Object.entries(attributes).filter(([, values]) => values.length > 0);
  const designCount = countDesignCombinations(attributes);

  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
      {/* Garment source */}
      <div className="space-y-3.5 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-100 pb-2.5">
          <span className="flex items-center text-xs font-bold text-gray-900">
            <span className="mr-2 h-2.5 w-2.5 rounded-full bg-secondary-500" />
            Garment
          </span>
        </div>
        <div className="relative aspect-[3/4] overflow-hidden rounded-xl border border-gray-100 bg-gray-50">
          {clothImage ? (
            <img src={clothImage} alt="Garment" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-gray-300">
              <i className="fas fa-image text-2xl" />
            </div>
          )}
          <div className="absolute bottom-2 left-2 rounded bg-black/60 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-white">
            Source
          </div>
        </div>
      </div>

      {/* Design selections */}
      <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-100 pb-2.5">
          <span className="flex items-center text-xs font-bold text-gray-900">
            <span className="mr-2 h-2.5 w-2.5 rounded-full bg-secondary-500" />
            Design Details
          </span>
          {designCount > 0 && (
            <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-secondary-700">
              {designCount} design{designCount === 1 ? '' : 's'}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Category</p>
            <p className="mt-1 text-sm font-semibold capitalize text-gray-900">{gender}</p>
          </div>
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Garment</p>
            <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-gray-900">
              {garment ? (
                <>
                  <span className="text-base">{getClothIcon(garment.id)}</span>
                  {garment.name}
                </>
              ) : (
                'Not selected'
              )}
            </p>
          </div>
        </div>

        <div>
          <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">Attributes</p>
          {attributeEntries.length > 0 ? (
            <div className="space-y-3">
              {attributeEntries.map(([label, values]) => (
                <div key={label}>
                  <p className="mb-1.5 text-[11px] font-semibold text-gray-500">{label}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {values.map((value) => (
                      <span
                        key={`${label}-${value}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-medium text-secondary-800"
                      >
                        {value}
                        <button
                          type="button"
                          onClick={() => onRemoveAttribute(label, value)}
                          className="text-secondary-600 transition-colors hover:text-red-500"
                          aria-label={`Remove ${value}`}
                          title={`Remove ${value}`}
                        >
                          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400">No attributes selected — the AI will design freely.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DesignerSummaryReview;
